import { AgCartesianChartOptions } from "ag-charts-community";

import {
  AG_CHARTS_BAR_CORNER_RADIUS,
  ANALYTICS_CUSTOM_SERIES_COLORS,
  CHART_SERIES_STROKE_WIDTH_NONE,
} from "../constants";

import { baseAnalyticsChartOptions } from "./base";

const DEFAULT_READ_ONLY_ANNOTATIONS = true;

const coerceReadOnlyAnnotations = (options: Record<string, unknown>): Record<string, unknown> => {
  const { annotations } = options;
  if (!Array.isArray(annotations)) {
    return options;
  }

  return {
    ...options,
    annotations: annotations.map(annotation => {
      if (!annotation || typeof annotation !== "object") {
        return annotation;
      }
      return {
        ...annotation,
        readOnly: (annotation as { readOnly?: boolean }).readOnly ?? DEFAULT_READ_ONLY_ANNOTATIONS,
      };
    }),
  };
};

const normalizeBarSeries = (
  series: Record<string, unknown>,
  index: number,
): Record<string, unknown> => {
  const { label: _label, ...rest } = series;
  return {
    ...rest,
    fill:
      rest.fill ?? ANALYTICS_CUSTOM_SERIES_COLORS[index % ANALYTICS_CUSTOM_SERIES_COLORS.length],
    strokeWidth: CHART_SERIES_STROKE_WIDTH_NONE,
    cornerRadius: AG_CHARTS_BAR_CORNER_RADIUS,
    label: { enabled: false },
  };
};

const normalizeChartSeries = (series: unknown): unknown => {
  if (!Array.isArray(series)) {
    return series;
  }

  return series.map((item, index) => {
    if (!item || typeof item !== "object") {
      return item;
    }

    const seriesItem = item as Record<string, unknown>;
    if (seriesItem.type === "bar") {
      return normalizeBarSeries(seriesItem, index);
    }

    if (seriesItem.label && typeof seriesItem.label === "object") {
      return {
        ...seriesItem,
        label: { ...(seriesItem.label as Record<string, unknown>), enabled: false },
      };
    }

    return seriesItem;
  });
};

const normalizeAgChartsOptions = (options: Record<string, unknown>): Record<string, unknown> => {
  const { title: _title, subtitle: _subtitle, series, legend, ...rest } = options;
  const normalizedSeries = normalizeChartSeries(series);
  const seriesCount = Array.isArray(normalizedSeries) ? normalizedSeries.length : 0;

  return {
    ...rest,
    series: normalizedSeries,
    legend:
      seriesCount <= 1
        ? { enabled: false }
        : (legend ?? {
            enabled: true,
            item: {
              marker: {
                shape: "circle",
              },
            },
          }),
  };
};

export const buildAgChartsOptionsChart = (
  agChartsOptions: Record<string, unknown>,
): AgCartesianChartOptions => {
  const safeOptions = coerceReadOnlyAnnotations(agChartsOptions);
  const normalizedOptions = normalizeAgChartsOptions(safeOptions);
  return {
    ...baseAnalyticsChartOptions,
    ...(normalizedOptions as AgCartesianChartOptions),
  };
};
